import type {
  ApiClient,
  AuthSession,
  BadgePort,
  ClipboardPort,
  DownloadPort,
  ExternalLinkPort,
  FilePickerPort,
  NetworkStatus,
  NotificationPort,
  RealtimeSubscriber,
  ShortcutRegistry,
} from '@sunucupark/client-core';
import { createContext, useContext, type ReactNode } from 'react';

/**
 * Everything the app injects once and every hook reads from here (ADR 0006).
 * Components never construct an `ApiClient` or reach for a platform API
 * themselves, so tests hand in fakes and the desktop shell hands in its own
 * adapters.
 */
export interface Client {
  readonly api: ApiClient;
  readonly session: AuthSession;
  readonly shortcuts: ShortcutRegistry;
  readonly network: NetworkStatus;
  /**
   * The live channel, for the few features that act on an event themselves
   * rather than only letting it invalidate a query (ADR 0008). Optional: a
   * shell without a stream leaves it out and {@link useRealtimeEvent} does
   * nothing.
   */
  readonly realtime?: RealtimeSubscriber;
  /** Platform capabilities; each is optional because not every shell has all of them. */
  readonly platform?: {
    readonly notifications?: NotificationPort;
    readonly badge?: BadgePort;
    readonly clipboard?: ClipboardPort;
    readonly links?: ExternalLinkPort;
    readonly files?: FilePickerPort;
    readonly downloads?: DownloadPort;
  };
}

const ClientContext = createContext<Client | null>(null);

export interface ClientProviderProps {
  client: Client;
  children: ReactNode;
}

/** Puts the wired-up client in context. One per signed-in app. */
export function ClientProvider({ client, children }: ClientProviderProps) {
  return <ClientContext.Provider value={client}>{children}</ClientContext.Provider>;
}

/** The injected client; throws when a component is rendered outside the provider. */
export function useClient(): Client {
  const client = useContext(ClientContext);
  if (!client) throw new Error('useClient was called outside ClientProvider');
  return client;
}

/** The gateway client on its own, for hooks that only make requests. */
export function useApi(): ApiClient {
  return useClient().api;
}
